import { FC, ReactElement, useCallback, useEffect } from 'react';

import { AccessToken } from '@okta/okta-auth-js';
import { useOktaAuth } from '@okta/okta-react';
import axios from 'axios';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { clearAuth, setAuth, setLoading, setToken } from '@/store/slices/authSlice';
import { setFilters } from '@/store/slices/preferencesSlice';
import { API_ENDPOINTS, BASE_URL } from '@/utils/constants/apiEndpoints';
import logger from '@/utils/Logger';

import LoadingPage from '@/pages/common/LoadingPage';
import { getVendorDisplayName } from '@/types/matchDevice';
import { FindOneUserResponse, UserRole } from '@/types/user';

const extractRoleFromAccessToken = (accessToken?: AccessToken): UserRole => {
  const roles = accessToken?.claims?.roles;
  if (!roles) throw new Error('User does not have an assigned role');
  return (Array.isArray(roles) ? roles[0] : roles) as UserRole;
};

const mapPractitionerToLabelValue = (practitioner: {
  given?: string;
  family?: string;
  id: string;
}) =>
  practitioner.id === 'null'
    ? { label: '-', value: 'null' }
    : {
        label: `${practitioner?.given ?? ''} ${practitioner?.family ?? ''}`.trim(),
        value: practitioner.id
      };

export const AuthProvider: FC<{ children: ReactElement }> = ({ children }) => {
  const { oktaAuth } = useOktaAuth();
  const dispatch = useAppDispatch();

  const { isAuthenticated, isLoading } = useAppSelector((state) => state.auth);

  const initializeAuth = useCallback(async () => {
    try {
      dispatch(setLoading(true));

      const isOktaAuthenticated = await oktaAuth.isAuthenticated();
      if (isOktaAuthenticated) {
        const userInfo = await oktaAuth.getUser();
        const oktaAccessToken = oktaAuth.getAccessToken() ?? null;

        const { data } = (await axios.get(`${BASE_URL}${API_ENDPOINTS.USERS.CURRENT_USER}`, {
          headers: { Authorization: `Bearer ${oktaAccessToken}` }
        })) as { data: { user: FindOneUserResponse } };

        const accessToken = (await oktaAuth.tokenManager.get('accessToken')) as AccessToken | undefined;

        dispatch(
          setAuth({
            isAuthenticated: true,
            user: {
              ...data.user,
              externalId: userInfo.sub,
              role: extractRoleFromAccessToken(accessToken ?? undefined)
            },
            token: oktaAccessToken
          })
        );

        const { filters } = data?.user ?? { filters: {} };

        dispatch(
          setFilters({
            clinics:
              filters?.organizations?.map((organization) => ({
                label: organization.name,
                value: organization.id
              })) ?? [],
            practitioners: filters?.practitioners?.map(mapPractitionerToLabelValue) ?? [],
            signingPractitioners:
              filters?.signingPractitioners?.map(mapPractitionerToLabelValue) ?? [],
            deviceTypes:
              filters?.deviceTypes?.map((deviceType) => ({
                label: deviceType,
                value: deviceType
              })) ?? [],
            vendors:
              filters?.vendors?.map((vendor) => ({
                label: getVendorDisplayName(vendor),
                value: vendor
              })) ?? [],
            patientStatuses:
              filters?.patientStatuses?.map((status) => ({
                label: status,
                value: status
              })) ?? []
          })
        );

        dispatch(setLoading(false));
      } else {
        logger.error('AuthProvider mounted but user is not authenticated');
        dispatch(clearAuth());
        dispatch(setLoading(false));
      }
    } catch (error) {
      console.error('Error during authentication:', error);
      dispatch(clearAuth());
      dispatch(setLoading(false));
    }
  }, [dispatch, oktaAuth]);

  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  useEffect(() => {
    const handleTokenRenewed = (key: string, newToken: AccessToken) => {
      if (key === 'accessToken') {
        dispatch(setToken(newToken.accessToken));
      }
    };

    oktaAuth.tokenManager.on('renewed', handleTokenRenewed);
    return () => {
      oktaAuth.tokenManager.off('renewed', handleTokenRenewed);
    };
  }, [dispatch, oktaAuth]);

  if (isLoading || !isAuthenticated) {
    return <LoadingPage />;
  }

  return <>{children}</>;
};
